import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI } from '../utils/api';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { toast } from 'sonner';
import {
  Mic, MicOff, Video as VideoIcon, VideoOff,
  PhoneOff, Monitor, MonitorOff, Users,
  Copy, Link2, Crown, X, UserMinus,
  ChevronLeft, VolumeX,
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';

// ── Remote Video Tile ──
const RemoteVideoTile = ({ videoTrack, audioTrack, uid, userName, isHost }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !videoTrack) return;
    while (containerRef.current.firstChild) containerRef.current.removeChild(containerRef.current.firstChild);
    videoTrack.play(containerRef.current);
    const fixStyle = () => {
      if (!containerRef.current) return;
      containerRef.current.querySelectorAll('video, div').forEach(v => {
        v.style.width = '100%'; v.style.height = '100%';
        v.style.objectFit = 'cover'; v.style.position = 'absolute';
        v.style.top = '0'; v.style.left = '0';
      });
    };
    setTimeout(fixStyle, 50);
    setTimeout(fixStyle, 300);
    return () => { try { videoTrack.stop(); } catch (e) {} };
  }, [videoTrack]);

  useEffect(() => {
    if (!audioTrack) return;
    const play = () => { try { audioTrack.play(); } catch (e) {} };
    play();
    const resume = () => { play(); document.removeEventListener('click', resume); };
    document.addEventListener('click', resume, { once: true });
    return () => { try { audioTrack.stop(); } catch (e) {} document.removeEventListener('click', resume); };
  }, [audioTrack]);

  const displayName = userName || `User ${uid}`;

  return (
    <div className="relative overflow-hidden rounded-xl bg-[#1E293B] aspect-video" data-testid={`meeting-remote-video-${uid}`}>
      <div ref={containerRef} className="absolute inset-0 bg-[#1E293B]" />
      {!videoTrack && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#4338CA] mx-auto flex items-center justify-center text-lg sm:text-xl font-bold text-white">
            {displayName.charAt(0).toUpperCase()}
          </div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 bg-black/60 px-2 py-0.5 rounded-full backdrop-blur-sm">
        {isHost && <Crown className="w-3 h-3 text-amber-400" />}
        <span className="text-white text-xs font-medium truncate max-w-[100px]">{displayName}</span>
      </div>
    </div>
  );
};

// ── Main MeetingRoomView ──
const MeetingRoomView = ({ meeting: initialMeeting, socket, onLeave }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [meeting, setMeeting] = useState(initialMeeting);
  const [phase, setPhase] = useState('lobby');
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [uidNameMap, setUidNameMap] = useState({});
  const [uidUserIdMap, setUidUserIdMap] = useState({});
  const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, connected, failed

  const clientRef = useRef(null);
  const localVideoRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const screenTrackRef = useRef(null);
  const mountedRef = useRef(true);
  const joinedRef = useRef(false);
  const uidRef = useRef(Math.floor(Math.random() * 100000) + 1);
  const lobbyVideoRef = useRef(null);
  const lobbyVideoTrackRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const isHost = meeting?.host_id === user?.id;
  const agoraChannelName = `meeting_${meeting?.id}`;

  const copyJoinLink = () => {
    const url = `${window.location.origin}/join/${meeting?.join_code}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Meeting link copied!')).catch(() => toast.error('Failed to copy'));
  };

  const upsertRemoteUser = useCallback((uid, patch) => {
    setRemoteUsers(prev => {
      const idx = prev.findIndex(u => u.uid === uid);
      if (idx >= 0) { const up = [...prev]; up[idx] = { ...up[idx], ...patch }; return up; }
      return [...prev, { uid, videoTrack: null, audioTrack: null, ...patch }];
    });
  }, []);

  const removeRemoteUser = useCallback((uid) => {
    setRemoteUsers(prev => prev.filter(u => u.uid !== uid));
  }, []);

  // ── Lobby: preview camera ──
  useEffect(() => {
    if (phase !== 'lobby') return;
    let track = null;
    const startPreview = async () => {
      try {
        track = await AgoraRTC.createCameraVideoTrack();
        lobbyVideoTrackRef.current = track;
        if (lobbyVideoRef.current) track.play(lobbyVideoRef.current);
      } catch (e) { console.warn('[Lobby] Camera preview failed:', e); }
    };
    startPreview();
    return () => { if (track) { try { track.stop(); track.close(); } catch (e) {} } lobbyVideoTrackRef.current = null; };
  }, [phase]);

  // ── Socket: meeting room events ──
  useEffect(() => {
    if (!socket || !meeting?.id) return;
    socket.emit('meeting_join_room', { meeting_id: meeting.id });
    const handleUpdate = (data) => {
      if (!data?.data?.meeting_id || data.data.meeting_id !== meeting.id) return;
      switch (data.type) {
        case 'meeting_user_info':
          setUidNameMap(prev => ({ ...prev, [data.data.agora_uid]: data.data.user_name }));
          setUidUserIdMap(prev => ({ ...prev, [data.data.agora_uid]: data.data.user_id }));
          break;
        case 'meeting_participant_joined':
          toast(`${data.data.name} joined`);
          refreshMeeting();
          break;
        case 'meeting_participant_left':
          toast(`${data.data.name || 'Someone'} left`);
          refreshMeeting();
          break;
        case 'meeting_ended':
          toast.info('The host ended the meeting');
          handleLeaveMeeting();
          break;
        case 'meeting_kicked':
          toast.error('You have been removed from the meeting');
          handleLeaveMeeting();
          break;
        case 'meeting_host_action':
          if ((data.data.action === 'mute_all' || data.data.action === 'mute_user') && !isHost) {
            const t = localAudioTrackRef.current;
            if (t) { t.setEnabled(false); setIsMuted(true); }
            toast.info('Host muted your microphone');
          }
          break;
        default: break;
      }
    };
    socket.on('update', handleUpdate);
    return () => { socket.off('update', handleUpdate); socket.emit('meeting_leave_room', { meeting_id: meeting.id }); };
  }, [socket, meeting?.id]);

  const refreshMeeting = async () => {
    try { const res = await meetingAPI.getMeeting(meeting.id); setMeeting(res.data); } catch (e) {}
  };

  // ── Poll participants-map API for reliable name resolution ──
  useEffect(() => {
    if (phase !== 'live' || !meeting?.id) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await meetingAPI.getParticipantsMap(meeting.id);
        if (!active) return;
        const map = res.data || {};
        const names = {}; const userIds = {};
        for (const [aUid, info] of Object.entries(map)) {
          names[aUid] = info.name;
          userIds[aUid] = info.user_id;
        }
        setUidNameMap(prev => ({ ...prev, ...names }));
        setUidUserIdMap(prev => ({ ...prev, ...userIds }));
      } catch (e) {}
      refreshMeeting();
    };
    poll();
    pollIntervalRef.current = setInterval(poll, 5000);
    return () => { active = false; if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [phase, meeting?.id]);

  // ── Join Agora ──
  const joinMeetingCall = async () => {
    if (lobbyVideoTrackRef.current) {
      lobbyVideoTrackRef.current.stop(); lobbyVideoTrackRef.current.close(); lobbyVideoTrackRef.current = null;
    }
    // Wait for mobile camera hardware to fully release
    await new Promise(r => setTimeout(r, 1000));
    setPhase('live');
    setCallStatus('connecting');

    const appId = process.env.REACT_APP_AGORA_APP_ID;
    if (!appId) { toast.error('Agora not configured'); return; }

    try {
      const uid = uidRef.current;

      // Get token (also stores agora_uid in DB for name mapping)
      let token = null;
      try { const { data } = await meetingAPI.getMeetingToken(meeting.id, uid); token = data.token; }
      catch (e) { console.warn('[Meeting] Token failed:', e.message); }

      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;

      // Track handlers
      client.on('user-published', async (ru, type) => {
        try { await client.subscribe(ru, type); } catch (e) {
          await new Promise(r => setTimeout(r, 1000));
          try { await client.subscribe(ru, type); } catch (e2) {}
        }
        if (type === 'video') upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack });
        if (type === 'audio') upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack });
      });
      client.on('user-unpublished', (ru, type) => {
        if (type === 'video') upsertRemoteUser(ru.uid, { videoTrack: null });
        if (type === 'audio') upsertRemoteUser(ru.uid, { audioTrack: null });
      });
      client.on('user-left', (ru) => removeRemoteUser(ru.uid));
      client.on('user-joined', (ru) => upsertRemoteUser(ru.uid, {}));

      // Reconnection
      client.on('connection-state-change', (cur, prev) => {
        console.log(`[Meeting] ${prev} → ${cur}`);
        if (cur === 'DISCONNECTED' && mountedRef.current) {
          setCallStatus('failed');
          toast.error('Connection lost, reconnecting...');
        }
        if (cur === 'RECONNECTING') setCallStatus('connecting');
        if (cur === 'CONNECTED' && prev === 'RECONNECTING') {
          setCallStatus('connected');
          toast.success('Reconnected!');
          for (const ru of client.remoteUsers) {
            if (ru.hasVideo) client.subscribe(ru, 'video').then(() => upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack })).catch(() => {});
            if (ru.hasAudio) client.subscribe(ru, 'audio').then(() => upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack })).catch(() => {});
          }
        }
      });

      client.on('token-privilege-will-expire', async () => {
        try { const { data } = await meetingAPI.getMeetingToken(meeting.id, uid); await client.renewToken(data.token); } catch (e) {}
      });

      // JOIN
      await client.join(appId, agoraChannelName, token, uid);
      if (!mountedRef.current) return;
      joinedRef.current = true;
      setCallStatus('connected');

      // Publish local tracks
      const toPublish = [];
      try {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        localAudioTrackRef.current = audioTrack;
        // Auto-mute participants (not host) on join
        if (isMuted || !isHost) {
          audioTrack.setEnabled(false);
          setIsMuted(true);
        }
        toPublish.push(audioTrack);
      } catch (e) { console.warn('[Meeting] Mic failed:', e); }

      if (!isVideoOff) {
        // Try creating camera with retries (mobile hardware release can be slow)
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const videoTrack = await AgoraRTC.createCameraVideoTrack({ encoderConfig: { width: 640, height: 480, frameRate: 15, bitrateMax: 400 } });
            localVideoTrackRef.current = videoTrack;
            toPublish.push(videoTrack);
            const playLocal = () => {
              if (!localVideoRef.current || !videoTrack) return;
              videoTrack.play(localVideoRef.current);
              setTimeout(() => {
                if (localVideoRef.current) {
                  localVideoRef.current.querySelectorAll('video, div').forEach(v => {
                    v.style.width = '100%'; v.style.height = '100%'; v.style.objectFit = 'cover';
                    v.style.position = 'absolute'; v.style.top = '0'; v.style.left = '0';
                  });
                }
              }, 100);
            };
            playLocal();
            setTimeout(playLocal, 500);
            setTimeout(playLocal, 2000);
            break; // success
          } catch (e) {
            console.warn(`[Meeting] Camera attempt ${attempt + 1} failed:`, e);
            if (attempt < 2) {
              await new Promise(r => setTimeout(r, 1500)); // wait before retry
            } else {
              setIsVideoOff(true);
              toast.info('Camera unavailable — tap the camera button to retry');
            }
          }
        }
      }

      if (toPublish.length > 0) await client.publish(toPublish);

      // Subscribe to users already in the channel
      for (const ru of client.remoteUsers) {
        if (ru.hasVideo) { try { await client.subscribe(ru, 'video'); upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack }); } catch (e) {} }
        if (ru.hasAudio) { try { await client.subscribe(ru, 'audio'); upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack }); } catch (e) {} }
      }

      // Periodic check: catch any remote users missed by event handlers (mobile workaround)
      checkIntervalRef.current = setInterval(() => {
        const c = clientRef.current;
        if (!c) return;
        for (const ru of c.remoteUsers) {
          setRemoteUsers(prev => {
            if (prev.find(u => u.uid === ru.uid)) return prev;
            // Missed user — subscribe
            if (ru.hasVideo) c.subscribe(ru, 'video').then(() => upsertRemoteUser(ru.uid, { videoTrack: ru.videoTrack })).catch(() => {});
            if (ru.hasAudio) c.subscribe(ru, 'audio').then(() => upsertRemoteUser(ru.uid, { audioTrack: ru.audioTrack })).catch(() => {});
            return [...prev, { uid: ru.uid, videoTrack: ru.videoTrack, audioTrack: ru.audioTrack }];
          });
        }
      }, 3000);

      // Socket name broadcast (backup for non-VPS environments)
      if (socket) {
        socket.emit('meeting_user_info', { meeting_id: meeting.id, agora_uid: uid, user_name: user?.name || 'Unknown' });
      }

      toast.success('Joined meeting');
    } catch (error) {
      console.error('[Meeting] Join failed:', error);
      setCallStatus('failed');
      toast.error('Failed to join meeting');
    }
  };

  // ── Controls ──
  const toggleMute = async () => {
    const t = localAudioTrackRef.current;
    if (t) { await t.setEnabled(isMuted); setIsMuted(!isMuted); }
  };

  const toggleVideo = async () => {
    const client = clientRef.current;
    if (!client) return;
    if (isVideoOff) {
      // Try to enable camera with retry
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          let t = localVideoTrackRef.current;
          if (!t) {
            t = await AgoraRTC.createCameraVideoTrack({ encoderConfig: { width: 640, height: 480, frameRate: 15, bitrateMax: 400 } });
            localVideoTrackRef.current = t;
            await client.publish(t);
          } else { await t.setEnabled(true); }
          if (localVideoRef.current) {
            t.play(localVideoRef.current);
            setTimeout(() => {
              if (localVideoRef.current) {
                localVideoRef.current.querySelectorAll('video, div').forEach(v => {
                  v.style.width = '100%'; v.style.height = '100%'; v.style.objectFit = 'cover';
                  v.style.position = 'absolute'; v.style.top = '0'; v.style.left = '0';
                });
              }
            }, 100);
          }
          setIsVideoOff(false);
          break; // success
        } catch (e) {
          console.warn(`[Meeting] Camera toggle attempt ${attempt + 1} failed:`, e);
          if (attempt === 0) {
            await new Promise(r => setTimeout(r, 1500));
          } else {
            toast.error('Camera unavailable — check permissions in browser settings');
          }
        }
      }
    } else {
      const t = localVideoTrackRef.current;
      if (t) { await t.setEnabled(false); setIsVideoOff(true); }
    }
  };

  const toggleScreenShare = async () => {
    const client = clientRef.current;
    if (!client) return;
    if (!isScreenSharing) {
      try {
        const result = await AgoraRTC.createScreenVideoTrack({ encoderConfig: '1080p_1' }, 'disable');
        const sv = Array.isArray(result) ? result[0] : result;
        screenTrackRef.current = sv;
        const cam = localVideoTrackRef.current;
        if (cam) await client.unpublish(cam);
        await client.publish(sv);
        sv.on('track-ended', () => stopScreenShare());
        setIsScreenSharing(true);
      } catch (e) { if (e.code !== 'PERMISSION_DENIED') toast.error('Failed to share screen'); }
    } else { await stopScreenShare(); }
  };

  const stopScreenShare = async () => {
    const client = clientRef.current;
    const st = screenTrackRef.current;
    if (st && client) { try { await client.unpublish(st); } catch (e) {} st.close(); screenTrackRef.current = null; }
    const cam = localVideoTrackRef.current;
    if (cam && client) { try { await client.publish(cam); if (!isVideoOff && localVideoRef.current) cam.play(localVideoRef.current); } catch (e) {} }
    setIsScreenSharing(false);
  };

  const handleMuteAll = async () => {
    try { await meetingAPI.hostAction(meeting.id, 'mute_all'); toast.success('Muted all'); } catch (e) { toast.error('Failed'); }
  };

  const handleKickUser = async (userId) => {
    try { await meetingAPI.kickParticipant(meeting.id, userId); toast.success('Removed'); refreshMeeting(); } catch (e) { toast.error('Failed'); }
  };

  const handleEndMeeting = async () => {
    try { await meetingAPI.endMeeting(meeting.id); } catch (e) {}
    handleLeaveMeeting();
  };

  const cleanupAgora = useCallback(async () => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    [localVideoTrackRef, localAudioTrackRef, screenTrackRef].forEach(ref => {
      if (ref.current) { try { ref.current.stop(); ref.current.close(); } catch (e) {} ref.current = null; }
    });
    if (clientRef.current) {
      try { clientRef.current.removeAllListeners(); await clientRef.current.leave(); } catch (e) {}
      clientRef.current = null;
    }
    joinedRef.current = false;
  }, []);

  const handleLeaveMeeting = async () => {
    await cleanupAgora();
    try { await meetingAPI.leaveMeeting(meeting.id); } catch (e) {}
    if (onLeave) onLeave(); else navigate('/');
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; cleanupAgora(); };
  }, [cleanupAgora]);

  const totalUsers = 1 + remoteUsers.length;
  const participants = meeting?.participants || [];

  const getGridClass = () => {
    if (totalUsers <= 1) return 'grid-cols-1';
    if (totalUsers <= 2) return 'grid-cols-2';
    if (totalUsers <= 4) return 'grid-cols-2';
    if (totalUsers <= 9) return 'grid-cols-3';
    if (totalUsers <= 16) return 'grid-cols-4';
    return 'grid-cols-4 sm:grid-cols-5';
  };

  // ═══════════ LOBBY ═══════════
  if (phase === 'lobby') {
    return (
      <div className="fixed inset-0 bg-[#0F172A] z-50 flex flex-col items-center justify-center" data-testid="meeting-lobby">
        <button onClick={() => { if (onLeave) onLeave(); else navigate('/'); }} className="absolute top-4 left-4 text-white/60 hover:text-white flex items-center gap-1" data-testid="lobby-back-btn">
          <ChevronLeft className="w-5 h-5" /> Back
        </button>
        <div className="w-full max-w-lg px-6 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white" data-testid="meeting-title">{meeting?.title}</h1>
            <p className="text-white/50 text-sm mt-1">{isHost ? 'You are the host' : `Hosted by ${meeting?.host_name || 'Unknown'}`}</p>
          </div>
          <div className="relative aspect-video bg-[#1E293B] rounded-2xl overflow-hidden mx-auto max-w-sm">
            <div ref={lobbyVideoRef} className="absolute inset-0" />
            {isVideoOff && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[#4338CA] flex items-center justify-center text-2xl font-bold text-white">{user?.name?.charAt(0).toUpperCase()}</div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => setIsMuted(!isMuted)} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isMuted ? 'bg-red-500' : 'bg-white/20'}`} data-testid="lobby-mic-toggle">
              {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
            </button>
            <button onClick={() => { setIsVideoOff(!isVideoOff); if (lobbyVideoTrackRef.current) lobbyVideoTrackRef.current.setEnabled(isVideoOff); }} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isVideoOff ? 'bg-red-500' : 'bg-white/20'}`} data-testid="lobby-video-toggle">
              {isVideoOff ? <VideoOff className="w-5 h-5 text-white" /> : <VideoIcon className="w-5 h-5 text-white" />}
            </button>
          </div>
          <div className="flex items-center justify-center gap-3">
            <div className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-white/60" />
              <span className="text-white font-mono text-sm tracking-wider" data-testid="meeting-join-code">{meeting?.join_code}</span>
            </div>
            <button onClick={copyJoinLink} className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2 text-white text-sm flex items-center gap-1.5 transition-colors" data-testid="copy-link-btn">
              <Copy className="w-4 h-4" /> Copy Link
            </button>
          </div>
          <div className="text-center text-white/40 text-sm">{participants.length} participant{participants.length !== 1 ? 's' : ''} in the room</div>
          <Button onClick={joinMeetingCall} className="w-full py-4 text-lg bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-xl font-semibold" data-testid="join-meeting-btn">
            {!isHost && <span className="text-xs opacity-70 block mt-0.5 font-normal">You'll be muted on join</span>}
            Join Meeting
          </Button>
        </div>
      </div>
    );
  }

  // ═══════════ LIVE ═══════════
  return (
    <div className="fixed inset-0 bg-[#0F172A] z-50 flex flex-col h-[100dvh]" data-testid="meeting-room-view">
      {/* Top bar */}
      <div className="bg-[#1E293B]/80 backdrop-blur-md px-4 py-2 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          {callStatus === 'connecting' ? (
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
          ) : callStatus === 'failed' ? (
            <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
          ) : (
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
          )}
          <div className="min-w-0">
            <h2 className="text-white font-semibold text-sm truncate" data-testid="meeting-live-title">{meeting?.title}</h2>
            <p className="text-white/50 text-xs">
              {callStatus === 'connecting' ? 'Connecting...' : callStatus === 'failed' ? 'Reconnecting...' : `${totalUsers} participant${totalUsers !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyJoinLink} className="text-white/60 hover:text-white p-2 rounded-lg hover:bg-white/10" data-testid="live-copy-link" title="Copy link"><Copy className="w-4 h-4" /></button>
          <button onClick={() => setShowParticipants(!showParticipants)} className={`p-2 rounded-lg transition-colors ${showParticipants ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`} data-testid="toggle-participants-panel"><Users className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <div className={`flex-1 p-2 overflow-auto ${showParticipants ? 'pr-0' : ''}`} style={{ paddingBottom: '100px' }} data-testid="meeting-video-grid">
          <div className={`grid gap-2 auto-rows-fr ${getGridClass()}`} style={{ minHeight: '200px' }}>
            {/* Local */}
            <div className="relative overflow-hidden rounded-xl bg-[#1E293B] aspect-video" data-testid="meeting-local-video">
              <div ref={localVideoRef} className="absolute inset-0 bg-[#1E293B]" />
              {(isVideoOff && !isScreenSharing) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#4338CA] flex items-center justify-center text-lg sm:text-xl font-bold text-white">{user?.name?.charAt(0).toUpperCase()}</div>
                </div>
              )}
              <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 bg-black/60 px-2 py-0.5 rounded-full backdrop-blur-sm">
                {isHost && <Crown className="w-3 h-3 text-amber-400" />}
                <span className="text-white text-xs font-medium">You</span>
                {isMuted && <MicOff className="w-3 h-3 text-red-400" />}
              </div>
            </div>
            {/* Remote */}
            {remoteUsers.map((ru) => {
              let remoteName = uidNameMap[ru.uid];
              if (!remoteName) {
                const userId = uidUserIdMap[ru.uid];
                if (userId) { const p = participants.find(pp => pp.user_id === userId); if (p) remoteName = p.name; }
              }
              return <RemoteVideoTile key={ru.uid} uid={ru.uid} videoTrack={ru.videoTrack} audioTrack={ru.audioTrack} userName={remoteName} isHost={meeting?.host_id === uidUserIdMap[ru.uid]} />;
            })}
          </div>
        </div>

        {/* Participants Panel */}
        {showParticipants && (
          <div className="w-72 bg-[#1E293B] border-l border-white/10 flex flex-col shrink-0" data-testid="participants-panel">
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold text-sm">Participants ({participants.length})</h3>
              <button onClick={() => setShowParticipants(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {isHost && (
              <div className="p-2 border-b border-white/10">
                <button onClick={handleMuteAll} className="w-full text-xs bg-white/10 hover:bg-white/20 text-white py-2 rounded-lg flex items-center justify-center gap-1.5" data-testid="mute-all-btn"><VolumeX className="w-3.5 h-3.5" /> Mute All</button>
              </div>
            )}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {participants.map((p) => (
                  <div key={p.user_id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/5">
                    <Avatar className="w-8 h-8"><AvatarFallback className="bg-[#4338CA] text-white text-xs">{p.name?.charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium truncate">{p.name} {p.user_id === user?.id ? '(You)' : ''}</p>
                      <p className="text-white/40 text-xs flex items-center gap-1">
                        {p.role === 'host' && <><Crown className="w-3 h-3 text-amber-400" /> Host</>}
                        {p.role === 'participant' && 'Participant'}
                      </p>
                    </div>
                    {isHost && p.user_id !== user?.id && (
                      <button onClick={() => handleKickUser(p.user_id)} className="text-white/30 hover:text-red-400 p-1" title="Remove" data-testid={`kick-${p.user_id}`}><UserMinus className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="fixed bottom-0 left-0 right-0 z-[99999] bg-[#1E293B]/90 backdrop-blur-md py-3 px-4" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }} data-testid="meeting-controls">
        <div className="flex items-center justify-center gap-3 sm:gap-4">
          <button onClick={toggleMute} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isMuted ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`} data-testid="meeting-mic-toggle">
            {isMuted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
          </button>
          <button onClick={toggleVideo} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isVideoOff ? 'bg-red-500' : 'bg-white/20 hover:bg-white/30'}`} data-testid="meeting-video-toggle">
            {isVideoOff ? <VideoOff className="w-5 h-5 text-white" /> : <VideoIcon className="w-5 h-5 text-white" />}
          </button>
          <button onClick={toggleScreenShare} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isScreenSharing ? 'bg-green-500' : 'bg-white/20 hover:bg-white/30'}`} data-testid="meeting-screen-share">
            {isScreenSharing ? <MonitorOff className="w-5 h-5 text-white" /> : <Monitor className="w-5 h-5 text-white" />}
          </button>
          {isHost ? (
            <button onClick={handleEndMeeting} className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600" data-testid="end-meeting-btn"><PhoneOff className="w-5 h-5 text-white" /></button>
          ) : (
            <button onClick={handleLeaveMeeting} className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600" data-testid="leave-meeting-btn"><PhoneOff className="w-5 h-5 text-white" /></button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingRoomView;
